/** Business types of the firm-level settings (same values as the API's client types). */
export enum BusinessType {
  Limited = 0,
  SoleTrader = 1,
  Partnership = 2,
  Llp = 3,
}

export interface NamedOption {
  id: number;
  name: string;
}

// ---- Accountants ----

export interface FirmAccountantRow {
  id: string;
  name: string;
  address: string;
  addressLine: string | null;
  addressLine2: string | null;
  city: string | null;
  county: string | null;
  postCode: string | null;
  country: string | null;
  accountingBody: string | null;
  accountingBodyName: string | null;
}

export interface FirmAccountantOptions {
  accountingBodies: string[];
  countries: string[];
}

export interface SaveFirmAccountantPayload {
  id: string | null;
  name: string;
  addressLine: string | null;
  addressLine2: string | null;
  city: string | null;
  county: string | null;
  postCode: string | null;
  country: string | null;
  accountingBody: string | null;
  accountingBodyName: string | null;
}

// ---- Default chart of accounts per business type ----

export interface AccountTypeOption {
  id: number;
  name: string;
  groupName: string;
  statementArea: number;
  /** The client types (0 limited, 1 sole trader, 2 partnership, 3 LLP) that may use it; absent when every type may. */
  clientTypes?: number[] | null;
}

export interface ChartTemplateOptions {
  businessTypes: NamedOption[];
  /** -1 all, 1 active, 0 inactive, 2 archived. */
  statuses: NamedOption[];
  accountTypes: AccountTypeOption[];
  vatCodes: string[];
}

export interface ChartTemplateAccountRow {
  id: string;
  businessType: number;
  accountName: string;
  category: string;
  group: string;
  code: string;
  accountTypeId: number;
  vatCode: string | null;
  isActive: boolean;
  isArchived: boolean;
  status: string;
  /** APT369: the code is in Capium's default chart for the business type (the grid colours it); a code the firm added is false. */
  isStandard?: boolean;
}

/**
 * BUG-011: an old Capium (3.0) account code and the account that replaced it in the current chart. The stored chart keeps the
 * current code scheme; `newName`/`newCategory`/`newGroup` are null when the chart looked in has no account at `newCode`.
 */
export interface LegacyAccountCodeRow {
  clientType: number;
  oldCode: string;
  oldName: string;
  newCode: string;
  newName: string | null;
  newCategory: string | null;
  newGroup: string | null;
}

export interface ChartTemplateQuery {
  search: string | null;
  status: number;
  accountTypeId: number | null;
}

export interface ChartTemplateExportQuery extends ChartTemplateQuery {
  /** 1 CSV, 2 Excel, 3 PDF. */
  format: number;
  isPrint?: boolean;
  ids: string[];
}

export interface SaveChartTemplateAccountPayload {
  id: string | null;
  businessTypes: number[];
  accountTypeId: number;
  accountName: string;
  accountCode: string;
  vatCode: string | null;
  isActive: boolean;
  isArchived: boolean;
}

// ---- Report notes (Limited UK-GAAP / FRS102 / FRS105, LLP, Partnership, Sole trader) ----

export type ReportNotesKey =
  'limited-uk-gaap' | 'limited-frs102' | 'limited-frs102-1a' | 'limited-frs105' | 'llp' | 'partnership' | 'sole-trader';

export interface ReportNotesSettings {
  reportPoints: string[];
  accountantReportType: number;
  accountantReport: string[];
  auditorReportType: number;
  auditorReport: string[];
  profitAndLossPolicies: string[];
  balanceSheetPolicies: string[];
}

export interface ReportNotesSettingsResponse {
  key: ReportNotesKey;
  settings: ReportNotesSettings;
  isCustomised: boolean;
  updatedAtUtc: string | null;
}

// ---- Report format ----

export interface ReportFormatSettings {
  fontName: string;
  titleFontSize: number;
  tableHeaderFontSize: number;
  reportBodyFontSize: number;
  headerAlignment: string;
  headerFontSize: number;
  noteBodyPadding: number;
  textHeadingPadding: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  printPageNumberInPdf: boolean;
  printPageNumberOnCoverPage: boolean;
  doubleUnderlineTotals: boolean;
  showDashForZero: boolean;
  hidePeriodStartOnCoverPage: boolean;
  printRegistrationNumberOnBalanceSheet: boolean;
  autoSaveJournals: boolean;
  manageAutoRounding: boolean;
  profitAndLossRoundingAccounts: (string | null)[];
  balanceSheetRoundingAccounts: (string | null)[];
}

export interface RoundingAccountOption {
  code: string;
  name: string;
}

export interface ReportFormatOptions {
  fonts: string[];
  headerAlignments: string[];
  profitAndLossAccounts: RoundingAccountOption[];
  balanceSheetAccounts: RoundingAccountOption[];
}

export interface ReportFormatResponse {
  settings: ReportFormatSettings;
  options: ReportFormatOptions;
  isCustomised: boolean;
  updatedAtUtc: string | null;
}

/** BUG-001: one catalogue note as a layout's firm default text shows it. */
export interface FirmNoteTextRow {
  section: number;
  noteKey: string;
  name: string;
  content: string;
  catalogueContent: string;
  isCustomised: boolean;
  /** BUG-001: the General Settings field (`reportPoint3`, `plPolicy5` ...) this note is, when the layout's screens have one. */
  control?: string | null;
  /** BUG-001: that field's default heading for the layout (what Reset Default restores as the Title). */
  defaultTitle?: string | null;
}

/** BUG-074: a client's Report Format (its own when `isClientSpecific`, otherwise the firm's). */
export interface ClientReportFormatResponse {
  settings: ReportFormatSettings;
  options: ReportFormatOptions;
  isClientSpecific: boolean;
  updatedAtUtc: string | null;
}

// ---- Report logo ----

export interface ReportLogoInfo {
  isEnabled: boolean;
  hasLogo: boolean;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  updatedAtUtc: string | null;
}

// ---- Data security (firm-wide kill switch over the per-client setting) ----

/** canEdit reflects the calling user's own permission - shown read-only to everyone except firm admins. */
export interface FirmDataSecurityResponse {
  isEnabled: boolean;
  canEdit: boolean;
  updatedAtUtc: string | null;
}

// ---- Email templates ----

export interface EmailTemplateRow {
  key: string;
  name: string;
  subject: string;
  body: string;
  isCustomised: boolean;
  updatedAtUtc: string | null;
}
